import { useEffect, useRef, useState } from 'react';
import {
  HARDWARE_LABEL_FIELDS,
  type HardwareLabelField,
  type HardwareSuggestion,
  type StoredPhotoManifest,
  type StoredReport,
} from '@cer-autofill/shared';
import {
  apiErrorReason,
  fetchPhotoBlobUrl,
  getHardwareSuggestions,
  requestHardwareSuggestions,
  reviewHardwareSuggestions,
} from '../api.js';

/** Review outside the edit draft: Apply saves a new pending revision. */
export function HardwareLabelSuggestions({
  report,
  photos,
  onApplied,
}: {
  report: StoredReport;
  photos: readonly StoredPhotoManifest[];
  onApplied: (revision: StoredReport) => void;
}) {
  const [index, setIndex] = useState(0);
  const [photoId, setPhotoId] = useState('');
  const [suggestion, setSuggestion] = useState<HardwareSuggestion | null>(null);
  const [accepted, setAccepted] = useState<HardwareLabelField[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const eligible = photos.filter(
    (p) =>
      p.promptKey === 'pv-inverter-model-details' &&
      p.attachmentSource !== 'dropbox-import' &&
      ['image/jpeg', 'image/png', 'image/webp'].includes(p.mimeType),
  );
  const selectedPhoto = eligible.find((p) => p.photoId === photoId);
  const row = report.submission.hardware.inverters[index];
  const fields = HARDWARE_LABEL_FIELDS.filter((field) => !row?.[field]?.trim());
  useEffect(() => {
    let cancelled = false;
    let url: string | undefined;
    setImage(null);
    setImageLoaded(false);
    if (selectedPhoto)
      void fetchPhotoBlobUrl(report.jobId, selectedPhoto.photoId)
        .then((value) => {
          url = value;
          if (!cancelled) setImage(value);
          else if (value.startsWith('blob:')) URL.revokeObjectURL(value);
        })
        .catch(() => {
          if (!cancelled) setError('The source photo could not be loaded.');
        });
    return () => {
      cancelled = true;
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [report.jobId, selectedPhoto?.photoId, selectedPhoto?.uploadedAt]);
  if (!report.jobId.startsWith('PVD') || !report.submission.hardware.inverters.length) return null;
  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      if (alive.current) setError(apiErrorReason(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  function reset() {
    setSuggestion(null);
    setAccepted([]);
    setError(null);
    setZoom(1);
  }
  const resultValid =
    suggestion?.sourceRevision === report.submittedAt &&
    suggestion.inverterIndex === index &&
    suggestion.photoId === photoId &&
    !!selectedPhoto;
  return (
    <section className="label-suggestions" aria-label="Inverter label suggestions">
      <h3>Read inverter label</h3>
      <p>
        Suggest missing make, model and serial number from a label photo. Review each reading before
        applying. Applying saves a new revision for normal QA review.
      </p>
      <div className="form-grid">
        <label>
          Inverter
          <select
            value={index}
            disabled={busy}
            onChange={(e) => {
              setIndex(Number(e.target.value));
              reset();
            }}
          >
            {report.submission.hardware.inverters.map((inv, i) => (
              <option key={i} value={i}>
                Inverter {i + 1}
                {inv.make ? ` · ${inv.make}` : ''}
                {inv.model ? ` ${inv.model}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          Photo of this inverter’s label
          <select
            value={photoId}
            disabled={busy}
            onChange={(e) => {
              setPhotoId(e.target.value);
              reset();
            }}
          >
            <option value="">Choose the photo for this unit…</option>
            {eligible.map((p) => (
              <option key={p.photoId} value={p.photoId}>
                {p.filename}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted">
        Only the selected photo is sent to our AI vision service when you request suggestions.
        Confirm it shows the selected inverter. Existing values are kept.
      </p>
      {!eligible.length && <p>Add an inverter label photo in Edit report first.</p>}
      {!fields.length && <p>Make, model and serial number are already filled.</p>}
      {(row?.quantity ?? 1) > 1 && (
        <p>Use one inverter row per physical unit before reading its serial number.</p>
      )}
      <button
        type="button"
        disabled={busy || !selectedPhoto || !fields.length || (row?.quantity ?? 1) > 1}
        onClick={() =>
          void run(async () => {
            const result = await requestHardwareSuggestions(
              report.jobId,
              report.submittedAt,
              index,
              photoId,
            );
            if (alive.current) {
              setSuggestion(result);
              setAccepted([]);
            }
          })
        }
      >
        {busy ? 'Working…' : 'Suggest missing details'}
      </button>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="label-suggestions__review">
        {image && (
          <div>
            <label>
              Photo zoom
              <input
                aria-label="Label photo zoom"
                type="range"
                min="1"
                max="4"
                step="0.25"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </label>
            <div className="label-suggestions__image">
              <img
                src={image}
                alt="Selected inverter label"
                onLoad={() => setImageLoaded(true)}
                onError={() => {
                  setImageLoaded(false);
                  setError(
                    'The source photo could not be displayed. Reload it before accepting any values.',
                  );
                }}
                style={{ width: `${zoom * 100}%`, maxWidth: 'none' }}
              />
            </div>
          </div>
        )}
        {resultValid && suggestion && (
          <div aria-live="polite">
            {['pending', 'reviewing'].includes(suggestion.status) && (
              <>
                <p>
                  Request in progress. Check the result shortly; retrying does not start a duplicate
                  extraction.
                </p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const result = await getHardwareSuggestions(
                        report.jobId,
                        report.submittedAt,
                        suggestion.id,
                      );
                      if (alive.current) setSuggestion(result);
                    })
                  }
                >
                  Check result
                </button>
              </>
            )}
            {suggestion.status === 'failed' && (
              <p>Extraction failed. You can retry or enter the details manually.</p>
            )}
            {suggestion.status === 'reviewed' && (
              <div>
                <p>These suggestions have been reviewed.</p>
                {suggestion.appliedRevision && (
                  <a
                    href={`/reports/${encodeURIComponent(report.jobId)}/${encodeURIComponent(suggestion.appliedRevision)}`}
                  >
                    Open saved revision
                  </a>
                )}
              </div>
            )}
            {suggestion.status === 'ready' && (
              <>
                {(suggestion.needsClearerPhoto || !suggestion.readings.length) && (
                  <p>
                    Some details could not be read. Take a closer, clearer label photo for missing
                    values.
                  </p>
                )}
                {suggestion.readings.map((reading) => (
                  <label className="label-suggestion" key={reading.field}>
                    <input
                      type="checkbox"
                      checked={accepted.includes(reading.field)}
                      disabled={busy || !!row?.[reading.field]?.trim()}
                      onChange={(e) =>
                        setAccepted((current) =>
                          e.target.checked
                            ? [...current, reading.field]
                            : current.filter((f) => f !== reading.field),
                        )
                      }
                    />
                    <strong>
                      {reading.field}: {reading.value}
                    </strong>
                    <span>Printed text: “{reading.printedText}”</span>
                  </label>
                ))}
                <p className="muted">
                  Select each value you have checked against the photo. Unselected suggestions will
                  be dismissed.
                </p>
                <button
                  type="button"
                  disabled={busy || (accepted.length > 0 && !imageLoaded)}
                  onClick={() =>
                    void run(async () => {
                      const result = await reviewHardwareSuggestions(
                        report.jobId,
                        report.submittedAt,
                        suggestion.id,
                        accepted,
                      );
                      if (!alive.current) return;
                      setSuggestion(result.suggestion);
                      if (result.revision) onApplied(result.revision);
                    })
                  }
                >
                  {accepted.length ? 'Apply selected as new revision' : 'Dismiss suggestions'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
